/**
 * posthogSurveyTrigger — opens a PostHog survey popover from a button.
 * Mechanism (eligibility, completion mirroring, the unavailable modal,
 * consent recovery): docs/integrations/posthog.md, "In-App Survey Trigger".
 *
 * Caller wires these params via x-data:
 *   - surveyId:          PostHog survey UUID
 *   - triggerEvent:      event name captured on open
 *   - triggerProperties: properties sent with the event (optional)
 *   - completeUrl:       Symfony endpoint that records completion (optional)
 *   - csrfToken:         CSRF token for the endpoint
 *   - autoOpen:          open the survey as soon as it can show (optional)
 *   - holdAutoOpen:      start with the open-on-load held (optional); clear
 *                        `holdAutoOpen` once nothing else, such as a modal,
 *                        owns the screen
 *   - unavailableModal:  page modal id to open when the survey cannot show
 *                        (optional; without it such a click does nothing)
 */
const SURVEYS_LOAD_TIMEOUT_MS = 5000;

export default function posthogSurveyTrigger({ surveyId, triggerEvent, triggerProperties = {}, completeUrl, csrfToken, autoOpen = false, holdAutoOpen = false, unavailableModal = null }) {
    return {
        ready: false,
        unavailable: false,
        holdAutoOpen,
        openPending: autoOpen,
        shownUnavailable: false,
        recording: false,
        polling: null,
        loadTimeout: null,
        consentListener: null,

        init() {
            if (!surveyId) return;
            this.$watch?.('holdAutoOpen', () => this.tryPendingOpen());
            if (unavailableModal) {
                this.consentListener = (event) => this.onConsentUpdated(event);
                window.addEventListener('privacyConsentUpdated', this.consentListener);
            }
            this.load();
        },

        destroy() {
            if (this.consentListener) window.removeEventListener('privacyConsentUpdated', this.consentListener);
            clearTimeout(this.loadTimeout);
            this.stopPolling();
        },

        load() {
            if (!window.posthog) {
                this.markUnavailable();
                return;
            }
            this.unavailable = false;

            this.loadTimeout = setTimeout(() => {
                if (!this.ready) this.markUnavailable();
            }, SURVEYS_LOAD_TIMEOUT_MS);

            const onLoaded = (surveys, context) => {
                clearTimeout(this.loadTimeout);
                if (context?.isLoaded === false || window.posthog.is_capturing?.() === false) {
                    this.markUnavailable();
                    return;
                }
                this.ready = true;
                this.unavailable = false;
                this.checkComplete();
                this.tryPendingOpen();
            };

            if (typeof window.posthog.onSurveysLoaded === 'function') {
                window.posthog.onSurveysLoaded(onLoaded);
            } else {
                onLoaded();
            }
        },

        markUnavailable() {
            this.unavailable = true;
            this.tryPendingOpen();
        },

        onConsentUpdated(event) {
            if (!this.unavailable || !event.detail?.consent?.analytics) return;
            this.openPending = this.openPending || this.shownUnavailable;
            // The consent manager activates the gated PostHog script right after it dispatches this event.
            queueMicrotask(() => this.load());
        },

        tryPendingOpen() {
            if (!this.openPending || this.holdAutoOpen) return;
            if (this.ready) {
                this.openPending = false;
                this.showSurvey();
            } else if (this.unavailable) {
                this.openPending = false;
                this.showUnavailable();
            }
        },

        open() {
            if (this.ready) {
                this.showSurvey();
            } else if (this.unavailable) {
                this.showUnavailable();
            } else {
                this.openPending = true;
            }
        },

        showSurvey() {
            if (!window.posthog) return;
            if (typeof window.posthog.capture === 'function') {
                window.posthog.capture(triggerEvent, triggerProperties);
            }
            if (typeof window.posthog.displaySurvey === 'function') {
                window.posthog.displaySurvey(surveyId);
            }
            this.startPolling();
        },

        showUnavailable() {
            if (!unavailableModal) return;
            this.shownUnavailable = true;
            this.$dispatch?.('open-modal', { modal: unavailableModal });
        },

        startPolling() {
            if (!completeUrl || this.polling) return;
            this.polling = setInterval(() => this.checkComplete(), 1500);
        },

        checkComplete() {
            if (!completeUrl || this.recording) return;

            const stored = window.posthog?.get_property?.('$stored_person_properties') ?? {};
            const responded = stored[`$survey_responded/${surveyId}`];
            if (!responded) return;

            this.stopPolling();
            this.recordComplete();
        },

        stopPolling() {
            if (this.polling) {
                clearInterval(this.polling);
                this.polling = null;
            }
        },

        async recordComplete() {
            this.recording = true;
            try {
                const body = new URLSearchParams({ _token: csrfToken });
                const res = await fetch(completeUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body,
                });
                if (!res.ok) {
                    throw new Error(`Survey-completion POST failed: ${res.status}`);
                }
                window.location.reload();
            } catch (err) {
                this.recording = false;
                if (window.Sentry && typeof window.Sentry.captureException === 'function') {
                    window.Sentry.captureException(err);
                } else {
                    console.warn('PostHog survey-completion POST failed:', err);
                }
            }
        },
    };
}
